import type { ComparisonPage } from '../types';

export const beprofitVsWarehouse: ComparisonPage = {
  slug: 'beprofit-vs-custom-data-warehouse',
  group: 'ecomm',
  ours: 'greyfield',
  theirs: 'beprofit',
  title: 'BeProfit vs a custom data warehouse: Shopify profit app or your own model?',
  metaTitle: 'BeProfit vs a Custom Data Warehouse (2026): Honest Comparison',
  description:
    'BeProfit shows Shopify contribution profit for free to a few hundred dollars a month. Here is when that is enough and when you need a warehouse.',
  keywords: ['beprofit alternative', 'beprofit vs', 'beprofit pricing', 'shopify profit app', 'shopify profit dashboard'],
  published: '2026-11-03',
  updated: '2026-11-03',
  tldr:
    'For a single Shopify store that wants contribution profit per order without any setup, BeProfit is a sensible, cheap choice with a free plan. A warehouse makes sense when several channels or stores must share one model, when you need your own metric definitions, or when the data has to be queryable in your own Snowflake.',
  differences: [
    { heading: 'Ready-made profit vs modeled profit', body: 'BeProfit calculates profit per order from revenue, product costs, shipping, fees, and ad spend out of the box. A warehouse lets you define those rules yourself.' },
    { heading: 'Who defines new metrics', body: 'BeProfit custom metrics are on its top plan and built by its team on request. In a warehouse, your team edits the dbt model.' },
    { heading: 'Where the data lives', body: 'BeProfit exports CSV, with an API on its top plan. A warehouse puts the data in Snowflake you own.' },
  ],
  sections: [
    {
      heading: 'What BeProfit does well',
      paragraphs: [
        'BeProfit, now owned by Viably, pulls Shopify orders, product costs, Meta and Google ad spend, and shipping into a profit dashboard, with per-SKU margin, break-even and pricing simulators, and inventory days remaining. It also supports WooCommerce and Amazon, and includes a read-only connection so you can ask Claude or ChatGPT about your numbers.',
      ],
    },
    {
      heading: 'Where it stops',
      paragraphs: [
        'TikTok Shop is visible only if it runs as a Shopify sales channel. We found no wholesale, ERP, or retail POS connectors. Lower plans cover one store and limit history to one or two years.',
        'Custom metrics are requested from support, reviewed, and built in a few business days, using only data BeProfit already tracks. That works for small changes. It is slow when finance needs to change how [refunds are dated](/blog/shopify-refund-date-vs-order-date) or how costs are applied over time.',
      ],
    },
  ],
  pickThem: [
    'You run one Shopify store and want profit per order today.',
    'You need break-even and pricing simulators more than custom reports.',
    'Your budget is free to a few hundred dollars a month.',
  ],
  pickUs: [
    'You sell across Shopify, Amazon, TikTok Shop, and wholesale and need one model.',
    'You run several stores or brands and need the same definitions on each.',
    'Your team needs to change metric logic without waiting on a vendor.',
    'You want the data queryable in your own warehouse.',
  ],
  byTeam: [
    { team: 'Single-store Shopify merchant', pick: 'theirs', why: 'Profit per order on a free or low-cost plan.' },
    { team: 'Shopify plus Amazon brand with a finance lead', pick: 'ours', why: 'Fee timing and refunds reconciled across both channels.' },
    { team: 'Multi-store or multi-brand operator', pick: 'ours', why: 'Shared definitions without per-store plans.' },
  ],
  faq: [
    {
      question: 'Does a custom warehouse replace BeProfit?',
      answer: 'Yes, if you need cross-channel profit, your own metric logic, or warehouse access. No, if you run one Shopify store and BeProfit’s ready-made profit numbers answer your questions; it is far cheaper.',
    },
    {
      question: 'Who owns BeProfit?',
      answer: 'Viably, an ecommerce funding marketplace, acquired BeProfit in October 2024 according to CB Insights. It is still sold under the BeProfit name.',
    },
    {
      question: 'How much does BeProfit cost?',
      answer: 'There is a free plan up to 150 orders a month. Paid plans start at $49 a month and rise with order volume, with an enterprise band for multi-store brands.',
    },
  ],
  related: ['trueprofit-vs-custom-data-warehouse', 'lifetimely-vs-custom-data-warehouse', 'sellerboard-vs-custom-data-warehouse'],
};
